/**
 * Wraps a block of text and clamps it to a configurable max height. When the
 * content overflows, a "Show more" toggle expands it; when it fits, the toggle
 * stays hidden so short descriptions render unchanged.
 *
 * Markup:
 *   <collapsible-text data-max-height="100">
 *     <div class="collapsible-text__content">…actual content…</div>
 *     <button class="collapsible-text__toggle"
 *             data-collapsed-label="Show more"
 *             data-expanded-label="Show less"></button>
 *   </collapsible-text>
 */
class CollapsibleTextComponent extends HTMLElement {
  /** @type {HTMLElement | null} */
  #content = null;
  /** @type {HTMLButtonElement | null} */
  #toggle = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    this.#content = this.querySelector('.collapsible-text__content');
    this.#toggle = this.querySelector('.collapsible-text__toggle');
    if (!this.#content || !this.#toggle) return;

    this.#applyLabel();
    this.#toggle.addEventListener('click', this.#onToggle);

    // Re-evaluate fit when the content reflows (font load, viewport resize, etc.).
    this.#resizeObserver = new ResizeObserver(() => this.#evaluateFit());
    this.#resizeObserver.observe(this.#content);
    this.#evaluateFit();
  }

  disconnectedCallback() {
    this.#toggle?.removeEventListener('click', this.#onToggle);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /** Threshold in pixels above which the toggle is shown. */
  get #maxHeight() {
    const raw = parseInt(this.dataset.maxHeight ?? '', 10);
    return Number.isFinite(raw) && raw > 0 ? raw : 100;
  }

  #evaluateFit() {
    if (!this.#content || !this.#toggle) return;
    // Compare the natural scroll height (always the full content) against the
    // configured max — small fudge factor so a 1px sub-pixel diff doesn't trigger.
    const fits = this.#content.scrollHeight <= this.#maxHeight + 1;
    this.toggleAttribute('data-fits', fits);
    if (fits) {
      this.removeAttribute('expanded');
      this.#toggle.hidden = true;
    } else {
      this.#toggle.hidden = false;
    }
  }

  #onToggle = () => {
    const expanded = this.toggleAttribute('expanded');
    if (this.#toggle) {
      this.#toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
    this.#applyLabel();
  };

  #applyLabel() {
    if (!this.#toggle) return;
    const expanded = this.hasAttribute('expanded');
    const label = expanded
      ? this.#toggle.dataset.expandedLabel
      : this.#toggle.dataset.collapsedLabel;
    if (label) this.#toggle.textContent = label;
    this.#toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }
}

if (!customElements.get('collapsible-text')) {
  customElements.define('collapsible-text', CollapsibleTextComponent);
}
